import cn from "classnames";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Text, Button, Loader } from "@rshbintech.dboul/core-ui";
import {
  SIGNING_STATUSES,
  DEFAULT_ERROR_TITLE,
  CUSTOM_ERROR_TITLES,
  CUSTOM_ERROR_KEYS,
  CUSTOM_ERROR_KEY,
} from "../constants";

import { getStatusAnnotation } from "./utils/get-status-annotation";
import { SigningWidgetImage } from "./signing-widget-image";
import {
  SIGNING_STATUSES_WIDGET_COLORS,
  SIGNING_WIDGET_BUTTON_TITLES,
  SIGNING_STATUSES_TITLES,
  SIGNING_STATUSES_SUBANNOTATIONS,
} from "./constants";
import { SigningSubAnnotationType, SigningWidgetProps } from "./types";
import styles from "./styles.scss";

export const SigningWidget = memo(
  ({
    status,
    error,
    signingType,
    documentsTotalCount,
    signedDocumentsCount,
    validDocumentsCount,
    documentsInfo,
    signDocuments,
    onReturn,
  }: SigningWidgetProps) => {
    const [errorKey, setErrorKey] = useState("");
    const [errorTitle, setErrorTitle] = useState("");

    const isSignButtonClicked = useRef(false);

    useEffect(() => {
      // если ошибок нет - заголовок и описание определяются по статусу
      if (!error) {
        return;
      }
      // сверяем, если пришел ключ ошибки, то у нее есть заголовок и описание

      const isErrorKey = CUSTOM_ERROR_TITLES[error as CUSTOM_ERROR_KEY];

      if (isErrorKey) {
        setErrorKey(error);
        setErrorTitle(isErrorKey);

        return;
      }

      // ошибки с бэка могут вернуть значение, а не сам ключ, ищем ключ по значению
      const entries = Object.entries(CUSTOM_ERROR_TITLES);
      const errorValueIndex = entries.findIndex(([, value]) => value === error);

      if (errorValueIndex !== -1) {
        setErrorKey(entries[errorValueIndex][0]);
        setErrorTitle(error);

        return;
      }
      // если неопознанная ошибка - она становится описанием, а заголовок дефолтный

      setErrorTitle(DEFAULT_ERROR_TITLE);
      setErrorKey("");
    }, [error]);

    const title =
      errorTitle ||
      SIGNING_STATUSES_TITLES[signingType][
        status as keyof typeof SIGNING_STATUSES
      ];

    const showButtonAndAnnotation =
      error !== CUSTOM_ERROR_KEYS.ACTIVE_CERTIFICATE_NOT_FOUND &&
      error !== CUSTOM_ERROR_KEYS.INVALID_PINCODE;

    const statusAnnotation = useMemo(
      () =>
        getStatusAnnotation(
          status,
          signingType,
          documentsTotalCount,
          signedDocumentsCount,
          validDocumentsCount,
          documentsInfo,
          errorTitle,
          errorKey,
          error
        ),

      [
        status,
        signingType,
        documentsTotalCount,
        documentsInfo,
        validDocumentsCount,
        signedDocumentsCount,
        errorTitle,
        errorKey,
        error,
      ]
    );

    const signButtonHandler = () => {
      if (isSignButtonClicked.current) {
        return;
      }

      isSignButtonClicked.current = true;
      signDocuments();
    };

    const button = useMemo(() => {
      switch (status) {
        case SIGNING_STATUSES.ERROR:
          return (
            <Button
              htmlType="button"
              className={styles["return-button"]}
              onClick={onReturn}
            >
              {SIGNING_WIDGET_BUTTON_TITLES[signingType].RETURN}
            </Button>
          );

        case SIGNING_STATUSES.SIGNING_STOPPED:
          return (
            <Button
              className={styles.button}
              onClick={signDocuments}
              htmlType="button"
            >
              {SIGNING_WIDGET_BUTTON_TITLES[signingType].SIGNING_STOPPED}
            </Button>
          );

        case SIGNING_STATUSES.CHECKING_SIGNING_POSSIBILITY:
          return <Loader className={styles.loader} />;

        case SIGNING_STATUSES.READY_TO_BE_SIGNED:
          return (
            <Button
              className={styles.button}
              onClick={signButtonHandler}
              htmlType="button"
            >
              {SIGNING_WIDGET_BUTTON_TITLES[signingType].SIGN_ALL}
            </Button>
          );

        default:
          return null;
      }
    }, [status, signDocuments, onReturn]);

    const hasToShowSubAnnotation = status === SIGNING_STATUSES.IS_SIGNING;

    return (
      <div
        className={cn(
          styles["documents-signature-widget"],
          styles[
            SIGNING_STATUSES_WIDGET_COLORS[
              status as keyof typeof SIGNING_STATUSES
            ]
          ]
        )}
      >
        <div className={styles.container}>
          <SigningWidgetImage status={status} />
          <Text
            className={styles["signing-status"]}
            size="large"
            weight="medium"
          >
            {title}
          </Text>
          <Text className={styles["signing-status-annotation"]} size="medium">
            {statusAnnotation}
          </Text>
          {showButtonAndAnnotation && button}
          {showButtonAndAnnotation && hasToShowSubAnnotation && (
            <Text className={styles["signing-status-annotation"]} size="medium">
              {
                SIGNING_STATUSES_SUBANNOTATIONS[signingType][
                  status as keyof SigningSubAnnotationType
                ]
              }
            </Text>
          )}
        </div>
      </div>
    );
  }
);
